import { GridColumn } from './GridColumn';
import { GridRow } from './GridRow';
import { JQueryHelper } from './JQueryHelper';
import { RenderArg } from './Settings';
import { ICellEditor, ICellEditorWithText } from './SimpleGridLight';

export class GridCell {
    private readonly row: GridRow;
    private readonly col: GridColumn;
    private _td: JQuery;
    private isReadonly = false;
    private editor: ICellEditor;
    private isInit = false;
    private isRendered = false;

    constructor(row: GridRow, col: GridColumn) {
        this.row = row;
        this.col = col;
    }

    public value(value?: any): any {
        if (typeof value === 'undefined') {
            return this.row.getValue(this.col);
        } else {
            this.row.setValue(this.col, value);
            this.validateInner(true);
            this.getColumn().validate(this);
        }
    }

    public getRow(): GridRow {
        return this.row;
    }

    public getColumn(): GridColumn {
        return this.col;
    }

    public canEdit(): boolean {
        return !this.readonly() && this.enabled() && this.editor != null;
    }

    public tableCell(): JQuery {
        return this._td || (this._td = this.row.tableRow().find('.simpleGrid-cell').eq(this.col.columnIndex()));
    }

    public css(style: CSSStyleDeclaration): void {
        this.tableCell().css(style as any);
    }

    public init(): void {
        this.editor = this.col.editor().getEditor(this.row, this.col);
    }

    private applyDynSetting(): void {
        const setting = this.col.getDynamicSetting();
        if (setting != null) {
            const data = this.row.getData();
            for (const dynProp in setting) {
                if (setting.hasOwnProperty(dynProp)) {
                    const dataProperty = setting[dynProp];
                    if (_.isFunction(dataProperty)) {
                        this[dynProp](dataProperty(this));
                    } else {
                        this[dynProp](data[dataProperty]);
                    }
                }
            }
        }
    }

    private stopRefresh = false;

    public refresh(redraw: boolean = false): void {
        if ((redraw && this.isInit && this.isRendered) || this.stopRefresh) {
            return;
        }

        if (!this.isInit) {
            this.isInit = true;
            this.tableCell().addClass(this.col.cellClassName());
            this.tableCell().css(this.col.cellCss());
            this.readonly(this.col.readonly());
            if (this.validateStatus === ValidateStatus.None) {
                this.validateInner();
            }
        }
        this.refreshImpl();
        let title = this.text();
        if (this.col.getTitle() != null) {
            title = this.col.getTitle()(new RenderArg(this.row, this.col));
        }
        this.tableCell().attr('title', title);
        this.drawValidationStatus();
    }

    private refreshImpl(): void {
        this.stopRefresh = true;
        this.applyDynSetting();
        this.stopRefresh = false;
        if (this.editor != null) {
            this.editor.refresh();
            return;
        }
        if (this.row.isRendered) {
            this.render();
        }
    }

    private render(): void {
        let content: JQuery | string;
        if (this.col.render() != null) {
            content = this.col.render()(new RenderArg(this.row, this.col));
        } else {
            content = $('<div>', { text: this.row.getValue(this.col) });
        }
        if (content instanceof jQuery) {
            this.tableCell().empty().append(content);
        } else {
            this.tableCell()
                .empty()
                .text(content as string);
        }

        this.isRendered = true;
    }

    public content(val?: JQuery): JQuery {
        if (typeof val === 'undefined') {
            return this.tableCell().contents() as JQuery<HTMLElement>;
        }
        this.tableCell().empty().append(val);
        return val;
    }

    public width(val: number): void {
        this.tableCell().width(val);
    }

    public preview(): void {
        if (this.canEdit()) {
            this.tableCell().addClass('active');
            this.editor.beginEdit(true);
        }
    }

    public exitPreview(): void {
        if (this.canEdit()) {
            this.tableCell().removeClass('active');
            this.editor.endEdit(true);
        }
    }

    public beginEdit(): void {
        if (this.canEdit()) {
            this.tableCell().addClass('active');
            this.editor.beginEdit();
        }
    }

    public endEdit(force: boolean): boolean {
        if (!this.tableCell().hasClass('active')) {
            return false;
        }
        if (!force && this.editor.isInActiveMode()) {
            return false;
        }
        this.editor.endEdit();
        this.tableCell().removeClass('active');
        this.preview();
        return true;
    }

    private isInEditMode(): boolean {
        return this.tableCell().hasClass('active');
    }

    public readonly(value?: boolean): boolean {
        if (arguments.length === 0) {
            return this.isReadonly;
        }
        this.isReadonly = value;
        this.refresh();
        return value;
    }

    public enabled(value?: boolean): boolean {
        if (arguments.length === 0) {
            return !this.readonly();
        }
        this.readonly(!value);
    }

    public getEditor<CellEditor extends ICellEditor>(): CellEditor {
        return this.editor as CellEditor;
    }

    public resetEditor(param?: any): void {
        this.endEdit(true);
        this.tableCell().empty();
        this.editor = this.col.editor().getEditor(this.row, this.col, param);
        this.editor.refresh();
    }

    public text(): string | any {
        if (this.editor != null) {
            if ('getText' in this.editor) {
                return ((this.editor as any) as ICellEditorWithText).getText();
            } else {
                return JQueryHelper.visibilityText(this.tableCell());
            }
        }
        if (this.col.render()) {
            const render = this.col.render()(new RenderArg(this.row, this.col, true));
            if (typeof render === 'string') {
                return render;
            }
            return JQueryHelper.visibilityText(render);
        }
        return this.value();
    }

    private lastErrors: string[];
    private validateStatus: ValidateStatus = ValidateStatus.None;

    public isValid(valid?: boolean): boolean {
        if (typeof valid !== typeof void 0) {
            this.validateStatus = valid ? ValidateStatus.Valid : ValidateStatus.Error;
            this.refresh();
        }
        return this.validateStatus !== ValidateStatus.Error;
    }

    public validate(): void {
        this.validateInner(true);
    }

    private validateInner(draw = false): void {
        const validator = this.getColumn().getValidator();
        if (validator != null) {
            const errors = validator(this);
            this.lastErrors = errors;
            if (errors && errors.length) {
                this.validateStatus = ValidateStatus.Error;
            } else {
                this.validateStatus = ValidateStatus.Valid;
            }
        } else {
            this.validateStatus = ValidateStatus.None;
        }
        if (draw) {
            this.drawValidationStatus();
        }
    }

    private drawValidationStatus(): void {
        const invalidCellValue = !this.isValid();
        let indicator = this.tableCell().find('span.invalid-cell-indicator');
        if (invalidCellValue) {
            if (indicator.length === 0) {
                indicator = $('<span>').addClass('invalid-cell-indicator').appendTo(this.tableCell());
            }
            indicator.attr('title', _(this.lastErrors).join('\r\n')).show();
        } else {
            indicator.hide();
        }
    }

    public canTab(): boolean {
        return this.canEdit() && this.getEditor().canTab && this.getEditor().canTab();
    }
}

const enum ValidateStatus {
    None,
    Valid,
    Error
}
